/**
 * Iter 234 · /airq — Public marketing landing.
 * Dark aviation aesthetic · cyan/teal accents · feature grid ·
 * dual login CTAs (operator + government).
 */
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  AirplaneTilt, ShieldCheck, MapPin, ChartLineUp, HeartStraight,
  Broadcast, Certificate, Clock, ArrowRight, Buildings,
} from "@phosphor-icons/react";

const FEATURES = [
  { icon: AirplaneTilt, title: "Fleet + Fleet-of-fleets",
    body: "Helicopter, fixed-wing, and drone assets in one console. Range-aware dispatch picks the right aircraft for every mission." },
  { icon: MapPin, title: "Pad-to-pad routing",
    body: "Helipad, dronepad, and hospital-rooftop registry with jurisdiction cascades (state → district → LSGD → ward)." },
  { icon: HeartStraight, title: "Aeromedical + Blood + Organ",
    body: "Patient evac, organ transfer, blood delivery, medicine drop, specialist shuttle — all with structured ePCR logging." },
  { icon: ShieldCheck, title: "DGCA compliance",
    body: "Append-only flight logs, expiring-cert alerts, mayday auto-escalation. Regulator sign-in with email OTP." },
  { icon: Broadcast, title: "Rules-first escalation",
    body: "Weather block, hospital-full, ATC deny — one tap surfaces ranked alt aircraft, alt pads, and alt hospitals." },
  { icon: ChartLineUp, title: "Founder telemetry",
    body: "Live KPIs and origin heatmap for missions across the ecosystem. Success rate, escalations, compliance debt." },
  { icon: Certificate, title: "CSR + Schemes",
    body: "Karunya / KASP / corporate wallets, per-customer eligibility, claim reconciliation, and CSR fund ledgers." },
  { icon: Buildings, title: "Multi-stakeholder",
    body: "Hospital, builder, insurance, regulator, and crew portals — every stakeholder sees only what they need." },
];

const ROLE_CARDS = [
  {
    title: "Operator portal",
    body: "Pad operators, aeromed admins. Manage helipads, aircraft, missions, crews, and compliance docs.",
    cta: "Operator sign-in",
    to: "/airq/login",
    color: "from-[#0F766E] to-[#164E63]",
    testid: "airq-cta-operator",
  },
  {
    title: "Government portal",
    body: "DGCA and State Aviation authorities. State-scoped clearances, registry, incidents, and pad compliance — protected by email OTP.",
    cta: "Government sign-in",
    to: "/airq/gov-login",
    color: "from-[#1E3A8A] to-[#0F172A]",
    testid: "airq-cta-gov",
  },
];


export default function AirqLanding() {
  const [scrolled, setScrolled] = useState(false);
  const [scrollY,  setScrollY]  = useState(0);
  useEffect(() => {
    const on = () => { setScrolled(window.scrollY > 40); setScrollY(window.scrollY); };
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  // Fade in feature cards once they enter viewport
  const featRef = useRef(null);
  const [featShown, setFeatShown] = useState(false);
  useEffect(() => {
    if (!featRef.current) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setFeatShown(true); io.disconnect(); }
    }, { threshold: 0.1 });
    io.observe(featRef.current);
    return () => io.disconnect();
  }, []);
  return (
    <div className="min-h-screen bg-[#020617] text-[#E2E8F0] font-body"
           data-testid="airq-landing-root"
           style={{ background: "radial-gradient(1200px 800px at 20% -10%, rgba(20,184,166,.12), transparent), radial-gradient(1000px 600px at 90% 20%, rgba(59,130,246,.10), transparent), #020617" }}>
      <style>{`
        @keyframes airqFadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .airq-fade-up {
          opacity: 0;
          animation: airqFadeUp 0.7s cubic-bezier(.22,.61,.36,1) forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .airq-fade-up { animation: none; opacity: 1; }
        }
      `}</style>      {/* Nav */}
      <nav className={`sticky top-0 z-50 transition-colors ${scrolled ? "bg-[#020617]/90 backdrop-blur border-b border-white/5" : "bg-transparent"}`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/airq" className="flex items-center gap-2">
            <AirplaneTilt size={22} className="text-[#67E8F9]"/>
            <div className="font-heading text-lg tracking-wide">Rescigo <span className="text-[#67E8F9]">AirQ</span></div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/airq/login" data-testid="nav-operator"
                    className="hidden md:inline-flex items-center gap-1 px-4 py-2 text-sm rounded-full border border-white/15 hover:bg-white/5 transition">
              Operator
            </Link>
            <Link to="/airq/gov-login" data-testid="nav-gov"
                    className="inline-flex items-center gap-1 px-4 py-2 text-sm rounded-full bg-[#67E8F9] text-[#0F172A] font-medium hover:bg-white transition">
              Government <ArrowRight size={13} weight="bold"/>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-12 md:pt-24 pb-16 relative overflow-hidden">
        {/* Parallax glow */}
        <div aria-hidden className="absolute pointer-events-none inset-0"
               style={{ transform: `translateY(${scrollY * 0.15}px)`, willChange:"transform" }}>
          <div className="absolute top-24 -left-24 w-72 h-72 rounded-full bg-[#67E8F9] opacity-[.08] blur-3xl"/>
          <div className="absolute top-56 right-0 w-96 h-96 rounded-full bg-[#A5B4FC] opacity-[.06] blur-3xl"/>
        </div>
        <div className="max-w-3xl relative"
               style={{ transform: `translateY(${scrollY * -0.05}px)`, willChange:"transform" }}>
          <span className="inline-block px-3 py-1 text-[11px] uppercase tracking-[.2em] rounded-full border border-[#67E8F9]/30 text-[#67E8F9] mb-6">
            Aeromedical · Drones · Regulator-grade
          </span>
          <h1 className="font-heading text-4xl md:text-6xl leading-[1.05] tracking-tight airq-fade-up"
                style={{ animationDelay: "0.05s" }}>
            The command centre for<br/>
            <span className="bg-gradient-to-r from-[#67E8F9] via-[#7DD3FC] to-[#A5B4FC] bg-clip-text text-transparent">
              airborne healthcare
            </span>.
          </h1>
          <p className="text-lg text-[#94A3B8] mt-6 max-w-2xl airq-fade-up"
               style={{ animationDelay: "0.2s" }}>
            Rescigo AirQ orchestrates helicopters, fixed-wing, and drones for
            patient evacuation, organ transfer, blood delivery, and medicine
            drops — with a real-time regulator surface that DGCA and state
            aviation authorities can trust.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8 airq-fade-up"
                 style={{ animationDelay: "0.35s" }}>
            <Link to="/airq/login" data-testid="hero-cta-operator"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0F172A] font-medium hover:bg-[#E2E8F0] transition">
              Operator sign-in <ArrowRight size={14} weight="bold"/>
            </Link>
            <Link to="/airq/gov-login" data-testid="hero-cta-gov"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-transparent border border-[#67E8F9] text-[#67E8F9] hover:bg-[#67E8F9]/10 transition">
              Government sign-in <ShieldCheck size={14}/>
            </Link>
          </div>
          <div className="flex items-center gap-4 mt-6 text-[11px] uppercase tracking-[.2em] text-[#64748B] airq-fade-up"
                 style={{ animationDelay: "0.5s" }}>
            <span className="flex items-center gap-1"><ShieldCheck size={12}/> DGCA-shaped compliance</span>
            <span className="flex items-center gap-1"><Clock size={12}/> Append-only flight log</span>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section ref={featRef} className="max-w-6xl mx-auto px-6 pb-20">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[.2em] text-[#67E8F9] mb-2">Platform</div>
          <h2 className="font-heading text-3xl md:text-4xl">Everything the fleet needs.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} data-testid={`feat-${i}`}
                     className={`p-5 rounded-xl border border-white/10 bg-white/[.03] hover:bg-white/[.06] hover:border-[#67E8F9]/30 transition ${featShown ? "airq-fade-up" : "opacity-0"}`}
                     style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
                <Icon size={24} className="text-[#67E8F9] mb-3"/>
                <div className="font-heading text-lg">{f.title}</div>
                <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Role cards */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[.2em] text-[#67E8F9] mb-2">Sign-in</div>
          <h2 className="font-heading text-3xl md:text-4xl">Two portals · one ecosystem.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ROLE_CARDS.map(r => (
            <Link key={r.to} to={r.to} data-testid={r.testid}
                    className={`group relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br ${r.color} p-8 hover:scale-[1.01] transition`}>
              <div className="relative z-10">
                <h3 className="font-heading text-2xl mb-2">{r.title}</h3>
                <p className="text-sm text-white/70 mb-6 max-w-md">{r.body}</p>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm text-white group-hover:bg-white group-hover:text-[#0F172A] transition">
                  {r.cta} <ArrowRight size={13} weight="bold"/>
                </div>
              </div>
              <AirplaneTilt size={140} className="absolute -right-6 -bottom-6 text-white/5"/>
            </Link>
          ))}
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-t border-white/10 py-12 mb-10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <StatBig v="5+" k="mission types"/>
          <StatBig v="OTP" k="DGCA sign-in"/>
          <StatBig v="P0—P2" k="priority tiers"/>
          <StatBig v="24/7" k="dispatch"/>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-[#64748B]">
        © Rescigo Health · AirQ ·
        <Link to="/" className="ml-2 text-[#94A3B8] hover:text-[#67E8F9]">back to Rescigo</Link>
      </footer>
    </div>
  );
}
function StatBig({ v, k }) {
  return (
    <div>
      <div className="font-heading text-3xl md:text-4xl bg-gradient-to-r from-[#67E8F9] to-[#A5B4FC] bg-clip-text text-transparent">{v}</div>
      <div className="text-[11px] uppercase tracking-[.2em] text-[#64748B] mt-2">{k}</div>
    </div>
  );
}
